import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

function embedUrl(u) {
  if (!u) return null;
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const loom = u.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  return null;
}

function tabCls(active) {
  return (
    "border border-black px-2 py-0.5 font-mono transition-colors " +
    (active ? "bg-black text-white" : "bg-white hover:bg-black hover:text-white")
  );
}

// Premium, maker-maintained spotlight for a claimed Starter listing: the maker's
// own positioning + a featured screenshot gallery with a lightbox (G2-style —
// every screenshot opens centered at a consistent size, with prev/next), and an
// optional video tab. Renders nothing without vendor content, so unclaimed pages
// are unchanged.
const tagCls2 = "absolute top-2 text-[10px] font-mono font-bold uppercase tracking-wide px-2 py-1 bg-[#0b0d11]/85 text-white pointer-events-none";

function CompareSlider({ before, after, alt, testid }) {
  const [pos, setPos] = useState(50);
  const [w, setW] = useState(0);
  const ref = useRef(null);
  const dragging = useRef(false);
  const update = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100)));
  };
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setW(el.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const onMove = (e) => { if (dragging.current) update(e.clientX); };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => { ro.disconnect(); window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
  }, []);
  return (
    <div
      ref={ref}
      onPointerDown={(e) => { dragging.current = true; update(e.clientX); }}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 4));
        if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 4));
      }}
      role="slider"
      tabIndex={0}
      aria-label="Drag to compare"
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      className="relative w-full border border-black overflow-hidden select-none cursor-ew-resize bg-[#0b0d11] outline-none"
      style={{ touchAction: "none" }}
      data-testid={testid}
    >
      <img src={after} alt={alt} draggable="false" className="block w-full h-auto pointer-events-none" />
      <div className="absolute inset-0 top-0 left-0 h-full overflow-hidden pointer-events-none" style={{ width: pos + "%" }}>
        <img src={before} alt="" draggable="false" className="absolute inset-0 h-full max-w-none pointer-events-none" style={{ width: w ? w + "px" : "100%" }} />
      </div>
      <span className={tagCls2 + " left-2 text-[#aeb6c0]"}>As filed on EDGAR</span>
      <span className={tagCls2 + " right-2"}>In EdgarReader</span>
      <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: pos + "%", transform: "translateX(-50%)" }}>
        <div className="w-[3px] h-full bg-white" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white text-[#0b0d11] flex items-center justify-center text-[14px] font-bold shadow">
          ⟷
        </div>
      </div>
    </div>
  );
}

export default function VendorSpotlight({ vendor, title }) {
  const normShot = (s) => (typeof s === "string" ? { url: s, caption: "" } : { ...s, url: s?.url || "", caption: s?.caption || "" });
  const shots = (Array.isArray(vendor?.screenshots) ? vendor.screenshots : []).map(normShot).filter((s) => s.url);
  const video = embedUrl(vendor?.video_url);
  const [tab, setTab] = useState(shots.length ? "shots" : "video");
  const [idx, setIdx] = useState(0);
  const [box, setBox] = useState(null); // lightbox index, or null when closed

  useEffect(() => {
    if (box === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setBox(null);
      else if (e.key === "ArrowRight") setBox((b) => (b === null ? b : (b + 1) % shots.length));
      else if (e.key === "ArrowLeft") setBox((b) => (b === null ? b : (b - 1 + shots.length) % shots.length));
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [box, shots.length]);

  if (!vendor) return null;
  const hasMedia = shots.length > 0 || !!video;
  if (!(vendor.positioning || hasMedia || vendor.integrations?.length)) return null;
  const lead = shots[Math.min(idx, Math.max(shots.length - 1, 0))];
  const open = (i) => { setIdx(i); setBox(i); };

  return (
    <section className="mt-5 space-y-5" data-testid="vendor-spotlight-hero">
      {vendor.positioning && (
        <div className="border border-black bg-white p-4" data-testid="vendor-positioning">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1">
            why {title}, from the maker
          </div>
          <p className="text-[14px] leading-relaxed text-[#111] m-0">{vendor.positioning}</p>
        </div>
      )}

      {hasMedia && (
        <div data-testid="vendor-gallery">
          {shots.length > 0 && video && (
            <div className="flex gap-2 mb-2 text-[12px]">
              <button type="button" onClick={() => setTab("shots")} className={tabCls(tab === "shots")} data-testid="vendor-tab-shots">
                [ screenshots ]
              </button>
              <button type="button" onClick={() => setTab("video")} className={tabCls(tab === "video")} data-testid="vendor-tab-video">
                [ video ]
              </button>
            </div>
          )}

          {tab === "video" && video ? (
            <div className="border border-black" style={{ aspectRatio: "16 / 9" }} data-testid="vendor-video">
              <iframe
                src={video}
                title={`${title} video`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          ) : lead ? (
            <>
              {lead.before && lead.after ? (
                <CompareSlider before={lead.before} after={lead.after} alt={lead.caption || `${title} comparison`} testid="vendor-compare-slider" />
              ) : (
                <button
                  type="button"
                  onClick={() => open(idx)}
                  className="group relative block w-full border border-black bg-[#fafafa] overflow-hidden"
                  style={{ aspectRatio: "16 / 9" }}
                  data-testid="vendor-screenshot-lead"
                  aria-label="enlarge screenshot"
                >
                  <img src={lead.url} alt={lead.caption || `${title} screenshot`} loading="lazy" className="w-full h-full object-contain" />
                  <span className="absolute bottom-0 right-0 bg-black text-white text-[10px] px-1.5 py-0.5 font-mono opacity-80 group-hover:opacity-100">
                    [ click to enlarge ]
                  </span>
                </button>
              )}
              {lead.caption && (
                <p className="mt-1.5 text-[13px] text-[#333] leading-snug" data-testid="vendor-screenshot-caption">
                  {lead.caption}
                </p>
              )}
              {shots.length > 1 && (
                <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="vendor-screenshot-thumbs">
                  {shots.map((s, i) => (
                    <button
                      type="button"
                      key={`${i}-${s.url}`}
                      onClick={() => open(i)}
                      className={"relative block border " + (i === idx ? "border-black ring-1 ring-black" : "border-[#bbb] hover:border-black")}
                      data-testid={`vendor-screenshot-thumb-${i}`}
                      aria-label={s.caption || `open screenshot ${i + 1}`}
                      title={s.caption || undefined}
                    >
                      <img src={s.url} alt="" loading="lazy" className="w-full h-16 object-cover block bg-[#fafafa]" />
                      <span className="absolute bottom-0 left-0 bg-black text-white text-[9px] px-1 font-mono">
                        [{String(i + 1).padStart(2, "0")}]
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {vendor.integrations?.length > 0 && (
        <div data-testid="vendor-integrations" className="text-[12px]">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#888] mr-2">integrations</span>
          {vendor.integrations.join(" · ")}
        </div>
      )}

      {box !== null && shots[box] && createPortal(
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center"
          onClick={() => setBox(null)}
          data-testid="vendor-lightbox"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setBox(null); }}
            className="absolute top-3 right-4 text-white text-[20px] font-mono hover:opacity-70 z-10"
            data-testid="vendor-lightbox-close"
            aria-label="close"
          >
            [×]
          </button>
          {shots.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setBox((box - 1 + shots.length) % shots.length); }}
              className="absolute left-2 sm:left-5 text-white text-[24px] font-mono hover:opacity-70 px-2 z-10"
              data-testid="vendor-lightbox-prev"
              aria-label="previous"
            >
              [‹]
            </button>
          )}
          {/* Fixed stage: every screenshot is scaled (up or down) to fill the same box */}
          <div className="w-[82vw] h-[82vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {shots[box].before && shots[box].after ? (
              <div className="w-full flex-1 min-h-0 flex items-center">
                <div className="w-full max-h-full">
                  <CompareSlider before={shots[box].before} after={shots[box].after} alt={shots[box].caption || `${title} comparison`} testid="vendor-lightbox-compare" />
                </div>
              </div>
            ) : (
              <img
                src={shots[box].url}
                alt={shots[box].caption || `${title} screenshot ${box + 1}`}
                className="w-full flex-1 min-h-0 object-contain"
              />
            )}
            {shots[box].caption && (
              <p className="mt-2 text-center text-white text-[13px] font-mono px-4" data-testid="vendor-lightbox-caption">
                {shots[box].caption}
              </p>
            )}
          </div>
          {shots.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setBox((box + 1) % shots.length); }}
              className="absolute right-2 sm:right-5 text-white text-[24px] font-mono hover:opacity-70 px-2 z-10"
              data-testid="vendor-lightbox-next"
              aria-label="next"
            >
              [›]
            </button>
          )}
          <span className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white text-[12px] font-mono">
            [ {box + 1} / {shots.length} ]
          </span>
        </div>,
        document.body
      )}
    </section>
  );
}

export function ClaimLine({ tool }) {
  if (tool.claimed) return null;
  return (
    <p className="mt-2 text-[12px] text-[#666]" data-testid="tool-claim-line">
      Work at {tool.title}?{" "}
      <Link to={`/claim/${tool.slug}`} className="tv-link font-bold">claim this listing &rarr;</Link> free, takes 30 seconds.
    </p>
  );
}
